import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  SpinnerGap,
  CheckCircle,
  XCircle,
  Clock,
  CalendarBlank,
  CurrencyDollar,
  CreditCard,
  Package,
  Lightning,
  Warning,
  ArrowClockwise,
  Trash,
  Plus,
  Receipt,
  ArrowRight,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [renewalPreview, setRenewalPreview] = useState(null);
  
  // Modal states
  const [cancelItemModalOpen, setCancelItemModalOpen] = useState(false);
  const [cancelAllModalOpen, setCancelAllModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch from CANONICAL entitlements endpoint first, then status/preview
      const [entitlementsRes, statusRes, previewRes] = await Promise.all([
        fetch(`${API}/subscription/entitlements`, { credentials: "include" }),
        fetch(`${API}/subscription/status`, { credentials: "include" }),
        fetch(`${API}/subscription/renewal-preview`, { credentials: "include" })
      ]);
      
      // Log canonical entitlements for debugging
      if (entitlementsRes.ok) {
        const entData = await entitlementsRes.json();
        console.log("[SubscriptionManagement] Canonical entitlements:", {
          tenant_id: entData.tenant_id,
          active: entData.active_subscription,
          bundles: entData.owned_bundle_ids?.length,
          features: entData.owned_feature_ids?.length
        });
      }
      
      if (statusRes.ok) {
        const data = await statusRes.json();
        console.log("[SubscriptionManagement] Status:", {
          has_subscription: data.has_subscription,
          status: data.status
        });
        setSubscriptionStatus(data);
      }
      
      if (previewRes.ok) {
        const data = await previewRes.json();
        setRenewalPreview(data);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      toast.error("Failed to load subscription information");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelItem = async () => {
    if (!selectedItem) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${API}/subscription/cancel-item`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ item_id: selectedItem.item_id }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Item scheduled for removal");
        setCancelItemModalOpen(false);
        setSelectedItem(null);
        fetchData();
      } else {
        toast.error(data.detail || data.error || "Failed to cancel item");
      }
    } catch (error) {
      toast.error("Failed to cancel item");
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateItem = async (itemId) => {
    setProcessing(true);
    
    try {
      const response = await fetch(`${API}/subscription/reactivate-item/${itemId}`, {
        method: "POST",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Item reactivated");
        fetchData();
      } else {
        toast.error(data.detail || data.error || "Failed to reactivate item");
      }
    } catch (error) {
      toast.error("Failed to reactivate item");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelAll = async () => {
    setProcessing(true);
    
    try {
      const response = await fetch(`${API}/subscription/cancel`, {
        method: "POST",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Subscription canceled");
        setCancelAllModalOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || data.error || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setProcessing(true);
    
    try {
      const response = await fetch(`${API}/subscription/reactivate`, {
        method: "POST",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Subscription reactivated");
        setReactivateModalOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || data.error || "Failed to reactivate subscription");
      }
    } catch (error) {
      toast.error("Failed to reactivate subscription");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 border">
          <Clock size={14} className="mr-1" />
          Canceling
        </Badge>
      );
    }
    
    const statusMap = {
      active: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle, label: "Active" },
      canceling: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, label: "Canceling" },
      past_due: { color: "bg-red-100 text-red-800 border-red-200", icon: Warning, label: "Past Due" },
      canceled: { color: "bg-zinc-100 text-zinc-800 border-zinc-200", icon: XCircle, label: "Canceled" },
      inactive: { color: "bg-zinc-100 text-zinc-500 border-zinc-200", icon: null, label: "No Subscription" },
    };
    
    const config = statusMap[status] || statusMap.inactive;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border`}>
        {Icon && <Icon size={14} className="mr-1" />}
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  const hasSubscription = subscriptionStatus?.has_subscription;
  const subscription = subscriptionStatus?.subscription;
  const items = subscription?.items || [];
  const isFullyCanceling = subscriptionStatus?.cancel_at_period_end;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="subscription-management-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="border-2 border-zinc-200"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
            Plans & Subscription
          </h1>
          <p className="text-zinc-500">Manage your subscription and billing</p>
        </div>
      </div>

      {/* No Subscription State */}
      {!hasSubscription && (
        <Card className="border-2 border-dashed">
          <CardContent className="p-8 text-center">
            <Package size={48} className="mx-auto text-zinc-300 mb-4" />
            <h3 className="font-heading text-lg font-bold uppercase mb-2">No Active Subscription</h3>
            <p className="text-zinc-500 mb-6">
              Unlock powerful features to grow your business
            </p>
            <Button
              onClick={() => navigate("/settings/upgrades")}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="browse-features-btn"
            >
              <Lightning size={18} className="mr-2" />
              Browse Features & Bundles
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Subscription */}
      {hasSubscription && (
        <>
          {/* Billing Overview Card */}
          <Card className={`border-2 ${
            subscriptionStatus?.status === 'past_due' ? 'border-red-300 bg-red-50' :
            isFullyCanceling ? 'border-amber-300 bg-amber-50' :
            'border-[#FF6600]/30 bg-gradient-to-r from-orange-50 to-amber-50'
          }`}>
            <CardContent className="p-6">
              {/* Cancellation Warning */}
              {isFullyCanceling && (
                <div className="flex items-center gap-3 p-4 mb-4 bg-amber-100 border border-amber-200 rounded-sm">
                  <Warning size={24} className="text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">Subscription Ending</p>
                    <p className="text-sm text-amber-700">
                      Your subscription is scheduled to end on {formatDate(subscription?.current_period_end)}.
                      You&apos;ll retain access until then.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setReactivateModalOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="reactivate-subscription-btn"
                  >
                    <ArrowClockwise size={16} className="mr-1" />
                    Keep Subscription
                  </Button>
                </div>
              )}

              {/* Past Due Warning */}
              {subscriptionStatus?.status === 'past_due' && (
                <div className="flex items-center gap-3 p-4 mb-4 bg-red-100 border border-red-200 rounded-sm">
                  <Warning size={24} className="text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Payment Failed</p>
                    <p className="text-sm text-red-700">
                      Your last payment was unsuccessful. Please update your payment method.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate("/settings/billing")}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Update Payment
                  </Button>
                </div>
              )}
              
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <h2 className="font-heading text-xl font-bold uppercase">Your Subscription</h2>
                    {getStatusBadge(subscriptionStatus?.status, isFullyCanceling)}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-zinc-500">Billing Cycle Start</p>
                      <p className="font-bold text-lg flex items-center gap-2">
                        <CalendarBlank size={18} className="text-[#FF6600]" />
                        {formatDate(subscription?.current_period_start)?.split(",")[0] || "-"}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-zinc-500">Next Payment</p>
                      <p className="font-bold text-lg flex items-center gap-2">
                        <CalendarBlank size={18} className="text-[#FF6600]" />
                        {isFullyCanceling ? "None" : (formatDate(subscription?.current_period_end)?.split(",")[0] || "-")}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-zinc-500">Monthly Total</p>
                      <p className="font-bold text-lg flex items-center gap-2">
                        <CurrencyDollar size={18} className="text-emerald-600" />
                        {renewalPreview?.total_renewal_display || subscriptionStatus?.renewal_amount?.total_display || "$0.00"}
                      </p>
                    </div>
                    
                    {subscriptionStatus?.payment_method && (
                      <div>
                        <p className="text-sm text-zinc-500">Payment Method</p>
                        <p className="font-bold text-lg flex items-center gap-2 capitalize">
                          <CreditCard size={18} className="text-zinc-600" />
                          •••• {subscriptionStatus.payment_method.last4}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => navigate("/settings/upgrades")}
                    className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                    data-testid="add-features-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Add Features
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/settings/billing")}
                    className="border-2"
                  >
                    <CreditCard size={16} className="mr-2" />
                    Billing & Payments
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Items */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Package size={20} className="text-[#FF6600]" />
                Subscription Items
              </h2>
              <span className="text-sm text-zinc-500">{items.length} item(s)</span>
            </div>

            {items.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-8 text-center">
                  <Lightning size={48} className="mx-auto text-zinc-300 mb-4" />
                  <p className="text-zinc-500">No items in your subscription</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const isCanceling = item.cancel_at_period_end || item.status === "canceling";
                  const details = item.item_details || {};
                  
                  return (
                    <Card
                      key={item.item_record_id || item.item_id}
                      className={`border-2 ${
                        isCanceling ? "border-amber-200 bg-amber-50/50" : "border-zinc-200"
                      }`}
                      data-testid={`subscription-item-${item.item_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                            isCanceling ? "bg-amber-100" : "bg-[#FF6600]/10"
                          }`}>
                            {item.item_type === "bundle" ? (
                              <Package size={24} className={isCanceling ? "text-amber-600" : "text-[#FF6600]"} />
                            ) : (
                              <Lightning size={24} className={isCanceling ? "text-amber-600" : "text-[#FF6600]"} />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium">{item.item_name || details.name || "Unknown Item"}</h3>
                              <Badge variant="outline" className="text-xs">
                                {item.item_type === "bundle" ? "Bundle" : "Feature"}
                              </Badge>
                              {isCanceling && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  Ending {formatDate(subscription?.current_period_end)?.split(",")[0]}
                                </Badge>
                              )}
                            </div>
                            {details.description && (
                              <p className="text-sm text-zinc-500 truncate">{details.description}</p>
                            )}
                            <p className="text-xs text-zinc-400 mt-1">
                              Added {formatDate(item.added_at)?.split(",")[0] || "recently"}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <p className={`font-bold text-lg ${isCanceling ? "text-zinc-400" : ""}`}>
                              ${(item.unit_price_cents / 100).toFixed(2)}
                              <span className="text-sm font-normal text-zinc-500">/mo</span>
                            </p>
                          </div>
                          
                          <div className="flex gap-2">
                            {isCanceling ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReactivateItem(item.item_id)}
                                disabled={processing}
                                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                data-testid={`reactivate-item-${item.item_id}`}
                              >
                                <ArrowClockwise size={16} className="mr-1" />
                                Keep
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setCancelItemModalOpen(true);
                                }}
                                className="text-zinc-500 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
                                data-testid={`cancel-item-${item.item_id}`}
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>

          {/* Renewal Preview */}
          {renewalPreview && !isFullyCanceling && (
            <section>
              <h2 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                <Receipt size={20} className="text-[#FF6600]" />
                Next Billing Preview
              </h2>
              
              <Card className="border-2">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {renewalPreview.will_renew?.length > 0 && (
                      <div>
                        <p className="text-sm text-zinc-500 mb-2">Items renewing:</p>
                        {renewalPreview.will_renew.map((item) => (
                          <div key={item.item_id} className="flex justify-between py-1">
                            <span>{item.item_name}</span>
                            <span className="text-zinc-600">{item.price_display}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {renewalPreview.will_cancel?.length > 0 && (
                      <div>
                        <p className="text-sm text-amber-600 mb-2">Items being removed:</p>
                        {renewalPreview.will_cancel.map((item) => (
                          <div key={item.item_id} className="flex justify-between py-1 text-zinc-400 line-through">
                            <span>{item.item_name}</span>
                            <span>{item.price_display}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex justify-between pt-3 border-t border-zinc-200 font-bold">
                      <span>Total on {formatDate(renewalPreview.next_billing_date)?.split(",")[0]}:</span>
                      <span className="text-[#FF6600]">{renewalPreview.total_renewal_display}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Cancel All Section */}
          {!isFullyCanceling && items.length > 0 && (
            <div className="pt-6 border-t border-zinc-200">
              <Button
                variant="outline"
                onClick={() => setCancelAllModalOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                data-testid="cancel-all-btn"
              >
                <XCircle size={18} className="mr-2" />
                Cancel All Paid Services
              </Button>
              <p className="text-xs text-zinc-500 mt-2">
                You&apos;ll retain access until the end of your current billing period.
              </p>
            </div>
          )}
        </>
      )}

      {/* Cancel Item Modal */}
      <AlertDialog open={cancelItemModalOpen} onOpenChange={setCancelItemModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase">Remove Item?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedItem && (
                <>
                  <strong>{selectedItem.item_name}</strong> will be removed at the end of your billing period
                  ({formatDate(subscription?.current_period_end)}).
                  <br /><br />
                  You&apos;ll retain access until then, and you won&apos;t be charged for this item on your next bill.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2">Keep Item</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelItem}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-item-btn"
            >
              {processing ? <SpinnerGap size={18} className="animate-spin" /> : "Remove Item"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel All Modal */}
      <AlertDialog open={cancelAllModalOpen} onOpenChange={setCancelAllModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase text-red-600">
              Cancel Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription>
              All your paid features will be removed at the end of your current billing period
              ({formatDate(subscription?.current_period_end)}).
              <br /><br />
              <strong>You&apos;ll retain access to all features until then.</strong>
              <br /><br />
              You can reactivate anytime before the period ends.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2">Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAll}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-all-btn"
            >
              {processing ? <SpinnerGap size={18} className="animate-spin" /> : "Cancel Subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Modal */}
      <AlertDialog open={reactivateModalOpen} onOpenChange={setReactivateModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase">
              Reactivate Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will continue as normal. You&apos;ll be charged
              {" "}<strong>{renewalPreview?.total_renewal_display || subscriptionStatus?.renewal_amount?.total_display}</strong>
              {" "}on {formatDate(subscription?.current_period_end)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReactivateSubscription}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-reactivate-btn"
            >
              {processing ? <SpinnerGap size={18} className="animate-spin" /> : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
